
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Clock, CreditCard } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import PsychologistCard from "@/components/PsychologistCard";

interface PsychologistInfo {
  name: string;
  photoUrl: string;
}

interface AppointmentCardProps {
  id: string;
  psychologist: PsychologistInfo;
  status: string;
  date: string;
  time: string;
  duration: number;
  meetingUrl?: string;
  paymentStatus?: string;
  totalPrice?: number;
}

const AppointmentCard = ({
  id,
  psychologist,
  status,
  date,
  time,
  duration,
  meetingUrl,
  paymentStatus = 'pending',
  totalPrice = 150
}: AppointmentCardProps) => {
  const { profile } = useAuth();
  const [processingPayment, setProcessingPayment] = useState(false);
  
  const formatDate = (dateStr: string) => {
    const date = parseISO(dateStr);
    return format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  };

  const getStatusBadge = (status: string) => {
    switch(status) {
      case "scheduled":
        return <Badge className="bg-green-500">Confirmada</Badge>;
      case "completed":
        return <Badge className="bg-blue-500">Concluída</Badge>;
      case "canceled":
        return <Badge variant="destructive">Cancelada</Badge>;
      case "pending_payment":
        return <Badge variant="outline" className="border-amber-500 text-amber-600">Aguardando pagamento</Badge>;
      default:
        return null;
    }
  };

  const handleMercadoPagoPayment = async () => {
    if (processingPayment) return;
    
    try {
      setProcessingPayment(true);
      
      const patientName = profile?.full_name || 
        (profile?.first_name && profile?.last_name ? `${profile.first_name} ${profile.last_name}` : 'Paciente');

      console.log('💳 Iniciando checkout do Mercado Pago para appointment:', id);
      toast.info('Criando checkout...', { description: 'Aguarde enquanto preparamos o pagamento' });

      const { data, error: checkoutError } = await supabase.functions.invoke('mercado-pago-checkout', {
        body: {
          appointmentId: id,
          amount: totalPrice,
          psychologistName: psychologist.name,
          patientName: patientName,
          appointmentDate: date,
          appointmentTime: time,
          duration: duration
        }
      });

      console.log('📨 Resposta do checkout:', { data, error: checkoutError });

      if (checkoutError) {
        console.error('❌ Erro no checkout:', checkoutError);
        throw new Error(checkoutError.message || 'Erro ao processar pagamento');
      }

      if (!data?.checkoutUrl) {
        console.error('❌ URL de checkout não retornada:', data);
        throw new Error('URL de checkout não foi gerada');
      }
      
      console.log('🚀 Redirecionando para:', data.checkoutUrl);
      toast.success('Checkout criado!', { description: 'Redirecionando para o Mercado Pago...' });
      
      // Redirecionar para o checkout do Mercado Pago
      setTimeout(() => {
        console.log('🔄 Executando redirecionamento...');
        window.location.href = data.checkoutUrl;
      }, 500);
      
    } catch (error: any) {
      console.error('❌ Erro no pagamento:', error);
      const errorMessage = error?.message || "Erro ao gerar pagamento. Tente novamente.";
      toast.error('Erro no pagamento', { description: errorMessage });
    } finally {
      setProcessingPayment(false);
    }
  };

  // Transform psychologist data for the card component
  const psychologistCardData = {
    id: psychologist.name.toLowerCase().replace(/\s+/g, '-'),
    name: psychologist.name,
    photo_url: psychologist.photoUrl,
    specialties: ['Psicologia Clínica'],
    price: totalPrice,
    bio: 'Psicólogo Clínico'
  };

  return (
    <Card className={status === "scheduled" || status === "pending_payment" ? "card-hover" : ""}>
      <CardContent className="pt-6">
        <div className="flex justify-between flex-wrap gap-4">
          <div className="flex-1">
            <PsychologistCard
              psychologist={psychologistCardData}
              variant="compact"
              showViewProfile={false}
              showBooking={false}
            />
          </div>
          <div>
            {getStatusBadge(status)}
          </div>
        </div>
        
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">{formatDate(date)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">{time} ({duration} min)</span>
          </div>
        </div>
        
        {(status === "pending_payment" || paymentStatus === 'pending') ? (
          <div className="mt-4">
            <Button 
              size="sm" 
              onClick={handleMercadoPagoPayment}
              disabled={processingPayment}
              className="w-full sm:w-auto"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              {processingPayment ? 'Processando...' : 'Realizar Pagamento'}
            </Button>
          </div>
        ) : status === "scheduled" && meetingUrl ? (
          <div className="mt-4">
            <Button size="sm" variant="outline" asChild>
              <a href={meetingUrl} target="_blank" rel="noopener noreferrer">
                Entrar na Consulta
              </a>
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default AppointmentCard;
